import { Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { MprcButton, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { GetUnblockRequestPublicationResponse } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    title: string;
    onClose: (saved: boolean | null) => void;
}

////////////////////////////////////////////////////////////////////////////////


export const RequestUnblockDialog: React.FC<IProps> = (props) => {
    const { publicationId, title, onClose } = props;

    const { t } = useTranslation("knowledge");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [viewData, setViewData] = useState<GetUnblockRequestPublicationResponse>({ message: "" } as GetUnblockRequestPublicationResponse);
    const [viewError, setViewError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const previousText = useRef<string>(undefined);

    ///
    const handleSave = () => {
        setSaving(true);
        api.v1.knowledgePublication.requestUnblock({ publicationId, message: viewData.message }).then(result => {
            if (result.data === true) {
                enqueueSnackbar(t("Feed.Card.Actions.RequestUnblockDialog.SaveSuccessMessage"), { variant: "success" });
                onClose(true);
            }
            else {
                enqueueSnackbar(t("Feed.Card.Actions.RequestUnblockDialog.SaveFailMessage"), { variant: "error" });
            }
        })
            .catch(e => setViewError(e))
            .finally(() => setSaving(false));
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.knowledgePublication.getUnblockRequest({ publicationId: publicationId });
                setViewData(result.data);
                previousText.current = result.data.message;
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }
        if (loading) getData();
    }, [loading]);

    return (<Dialog
        open
        onClose={() => onClose(null)}
        maxWidth="xs"
        fullWidth
    >
        <MprcDialogTitleClose
            title={t("Feed.Card.Actions.RequestUnblockDialog.Title")}
            subtitle={title}
            onClose={() => onClose(null)}
            shrinkPadding
        />
        <DialogContent>
            <MprcRequestIndicator type={loading ? "request" : "post"} visible={saving || loading} />
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
            <TextField
                autoFocus
                value={viewData.message}
                placeholder={t("Feed.Card.Actions.RequestUnblockDialog.MessagePlaceholder")}
                multiline={true}
                rows={10}
                onChange={(e) => setViewData({ ...viewData, message: e.target.value })}
                fullWidth
                variant="outlined"
            />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", alignItems: "center" }}>
            <MprcButton
                text={viewData.publicationActionId == undefined ? t("Feed.Card.Actions.RequestUnblockDialog.SaveButtonText") : t("Feed.Card.Actions.RequestUnblockDialog.EditButtonText")}
                title={viewData.publicationActionId == undefined ? t("Feed.Card.Actions.RequestUnblockDialog.SaveButtonText") : t("Feed.Card.Actions.RequestUnblockDialog.EditButtonText")}
                color="success"
                variant="contained"
                onClick={handleSave}
                disabled={!viewData.message || previousText.current == viewData.message}
            />
        </DialogActions>
    </Dialog>);
};
